import axios, { type AxiosInstance, type AxiosResponse } from 'axios';
import type {
  AuthenticationResult,
  LoginRequest,
  RefreshTokenRequest,
  LogoutRequest,
  UserProfile,
  SessionValidationResponse,
  TelegramUser,
  QuotaUsage,
} from '@/types/auth';

interface ApiError {
  message: string;
  response?: {
    data?: AuthenticationResult;
  };
}

class AuthApiService {
  private client: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = import.meta.env.VITE_API_BASE_URL || '';

    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      config => {
        const session = this.getStoredSession();
        if (session?.token) {
          config.headers.Authorization = `Bearer ${session.token}`;
        } else if (session?.sessionId) {
          // Fallback to sessionId for backward compatibility
          config.headers.Authorization = `Bearer ${session.sessionId}`;
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // Try to refresh session
          try {
            const session = this.getStoredSession();
            if (session?.sessionId) {
              await this.refreshSession({
                sessionId: session.sessionId,
                refreshToken: session.sessionId, // Simplified
              });
              // Retry original request
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, clear session and redirect to auth
            this.clearStoredSession();
            window.location.href = '/auth';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private getStoredSession() {
    try {
      const sessionData = localStorage.getItem('mcp_session');
      return sessionData ? JSON.parse(sessionData) : null;
    } catch {
      return null;
    }
  }

  private clearStoredSession() {
    localStorage.removeItem('mcp_session');
  }

  async login(request: LoginRequest): Promise<AuthenticationResult> {
    try {
      const response: AxiosResponse<any> = await this.client.post('/api/auth/login', request);

      // Backend returns 'user' field, frontend expects 'session'
      if (response.data.success && response.data.user) {
        // Map user to session with proper structure
        const sessionData = {
          ...response.data.user,
          isAuthenticated: true,
          expiresAt: response.data.expiresAt || response.data.user.expiresAt,
          token: response.data.token, // Store the JWT token
        };

        // Store session data
        localStorage.setItem('mcp_session', JSON.stringify(sessionData));

        return {
          success: true,
          session: sessionData,
          token: response.data.token,
          expiresAt: response.data.expiresAt,
          frontendUrl: response.data.frontendUrl,
        };
      }

      return response.data;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('Login API error:', apiError);

      if (apiError.response?.data) {
        return apiError.response.data as AuthenticationResult;
      }

      return {
        success: false,
        error: apiError.message || 'Login failed',
      };
    }
  }

  async logout(request: LogoutRequest): Promise<{ success: boolean; error?: string }> {
    try {
      await this.client.post('/api/auth/logout', request);

      // Clear local session data
      this.clearStoredSession();

      return { success: true };
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('Check auth API error:', apiError);

      // Clear local session even if API call fails
      this.clearStoredSession();

      return {
        success: false,
        error: apiError.message || 'Logout failed',
      };
    }
  }

  async refreshSession(request: RefreshTokenRequest): Promise<AuthenticationResult> {
    try {
      const response: AxiosResponse<any> = await this.client.post('/api/auth/refresh', request);

      if (response.data.success && response.data.session) {
        // Update stored session data with new token
        const updatedSession = {
          ...response.data.session,
          token: response.data.token, // Ensure new token is stored
        };
        localStorage.setItem('mcp_session', JSON.stringify(updatedSession));

        return {
          success: true,
          session: response.data.session,
          token: response.data.token,
          expiresAt: response.data.expiresAt,
        };
      }

      return response.data;
    } catch (error: any) {
      console.error('Session refresh API error:', error);

      if (error.response?.data) {
        return error.response.data;
      }

      return {
        success: false,
        error: error.message || 'Session refresh failed',
      };
    }
  }

  async validateSession(): Promise<SessionValidationResponse> {
    try {
      const response: AxiosResponse<any> = await this.client.post('/api/auth/validate');

      if (response.data.success && response.data.session) {
        // Update stored session data
        localStorage.setItem('mcp_session', JSON.stringify(response.data.session));

        return {
          valid: true,
          session: response.data.session,
          needsRefresh: false,
          remainingTime: response.data.remainingTime,
        };
      }

      return {
        valid: false,
        needsRefresh: false,
      };
    } catch (error: any) {
      console.error('Session validation API error:', error);

      return {
        valid: false,
        needsRefresh: error.response?.status === 401,
      };
    }
  }

  async telegramAuth(telegramData: TelegramUser): Promise<AuthenticationResult> {
    try {
      const response: AxiosResponse<any> = await this.client.post(
        '/api/auth/telegram',
        telegramData
      );

      if (response.data.success && response.data.user) {
        // Map user to session with proper structure
        const sessionData = {
          ...response.data.user,
          isAuthenticated: true,
          expiresAt: response.data.expiresAt || response.data.user.expiresAt,
        };

        // Store session data
        localStorage.setItem('mcp_session', JSON.stringify(sessionData));

        return {
          success: true,
          session: sessionData,
          token: response.data.token,
          expiresAt: response.data.expiresAt,
          frontendUrl: response.data.frontendUrl,
        };
      }

      return response.data;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('Telegram auth API error:', apiError);

      if (apiError.response?.data) {
        return apiError.response.data;
      }

      return {
        success: false,
        error: apiError.message || 'Telegram authentication failed',
      };
    }
  }

  async updateProfile(updates: Partial<UserProfile>): Promise<UserProfile | null> {
    try {
      const response: AxiosResponse<{ success: boolean; data: UserProfile }> =
        await this.client.put('/api/user/profile', updates);

      if (response.data.success) {
        return response.data.data;
      }

      return null;
    } catch (error: any) {
      console.error('Profile update API error:', error);
      throw new Error(error.response?.data?.message || 'Failed to update profile');
    }
  }

  async getProfile(): Promise<UserProfile | null> {
    try {
      const response: AxiosResponse<{ success: boolean; data: UserProfile }> =
        await this.client.get('/api/user/profile');

      if (response.data.success) {
        return response.data.data;
      }

      return null;
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('Get profile API error:', apiError);
      return null;
    }
  }

  async getQuotaUsage(): Promise<QuotaUsage> {
    try {
      const response: AxiosResponse<{ success: boolean; data: QuotaUsage }> =
        await this.client.get('/api/user/usage');

      if (response.data.success) {
        return response.data.data;
      }

      // Return default quota usage if API fails
      return {
        vectorDocuments: { current: 0, limit: 1000, percentage: 0 },
        apiCalls: { currentHour: 0, limit: 100, percentage: 0 },
        storage: { currentBytes: 0, limitBytes: 100_000_000, percentage: 0 },
        conversations: { currentDay: 0, limit: 50, percentage: 0 },
        toolExecutions: { currentHour: 0, limit: 200, percentage: 0 },
      };
    } catch (error: any) {
      console.error('Get quota usage API error:', error);

      // Return default quota usage
      return {
        vectorDocuments: { current: 0, limit: 1000, percentage: 0 },
        apiCalls: { currentHour: 0, limit: 100, percentage: 0 },
        storage: { currentBytes: 0, limitBytes: 100_000_000, percentage: 0 },
        conversations: { currentDay: 0, limit: 50, percentage: 0 },
        toolExecutions: { currentHour: 0, limit: 200, percentage: 0 },
      };
    }
  }

  async uploadProfilePhoto(
    formData: FormData
  ): Promise<{ success: boolean; user?: any; error?: string }> {
    try {
      const response = await this.client.post('/api/user/profile/photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        success: true,
        user: response.data.user,
      };
    } catch (error: unknown) {
      const apiError = error as ApiError;
      console.error('Update profile API error:', apiError);
      return {
        success: false,
        error: apiError.message || 'Profile update failed',
      };
    }
  }

  async deleteAccount(): Promise<{ success: boolean; error?: string }> {
    try {
      await this.client.delete('/api/user/account');

      // Clear local session data
      this.clearStoredSession();

      return { success: true };
    } catch (error: any) {
      console.error('Delete account API error:', error);

      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete account',
      };
    }
  }

  async verifyToken(token: string): Promise<AuthenticationResult> {
    try {
      const response: AxiosResponse<any> = await this.client.post('/api/auth/login', { token });

      if (response.data.success && response.data.user) {
        // Map user to session with proper structure
        const sessionData = {
          ...response.data.user,
          isAuthenticated: true,
          expiresAt: response.data.expiresAt || response.data.user.expiresAt,
        };

        // Store session data
        localStorage.setItem('mcp_session', JSON.stringify(sessionData));

        return {
          success: true,
          session: sessionData,
          token: response.data.token,
          expiresAt: response.data.expiresAt,
          frontendUrl: response.data.frontendUrl,
        };
      }

      return response.data;
    } catch (error: any) {
      console.error('Token verification API error:', error);

      if (error.response?.data) {
        return error.response.data;
      }

      return {
        success: false,
        error: error.message || 'Token verification failed',
      };
    }
  }

  async getServerHealth(): Promise<{ status: string; uptime: number }> {
    try {
      const response = await this.client.get('/api/health');
      return response.data;
    } catch (error: any) {
      console.error('Health check API error:', error);
      return { status: 'unhealthy', uptime: 0 };
    }
  }

  // Utility methods
  getApiUrl(endpoint: string): string {
    return `${this.baseURL}${endpoint}`;
  }

  setAuthToken(token: string): void {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  clearAuthToken(): void {
    delete this.client.defaults.headers.common['Authorization'];
  }

  // File upload for profile pictures
  async uploadProfilePicture(
    file: File
  ): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await this.client.post('/api/user/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error: any) {
      console.error('Avatar upload API error:', error);

      return {
        success: false,
        error: error.response?.data?.message || 'Failed to upload avatar',
      };
    }
  }

  // Get user sessions
  async getUserSessions(): Promise<any[]> {
    try {
      const response = await this.client.get('/api/user/sessions');
      return response.data.data || [];
    } catch (error: any) {
      console.error('Get sessions API error:', error);
      return [];
    }
  }

  // Revoke specific session
  async revokeSession(sessionId: string): Promise<{ success: boolean; error?: string }> {
    try {
      await this.client.delete(`/api/user/sessions/${sessionId}`);
      return { success: true };
    } catch (error: any) {
      console.error('Revoke session API error:', error);

      return {
        success: false,
        error: error.response?.data?.message || 'Failed to revoke session',
      };
    }
  }

  // Two-factor authentication methods (for future use)
  async enableTwoFactor(): Promise<{
    success: boolean;
    qrCode?: string;
    secret?: string;
    error?: string;
  }> {
    try {
      const response = await this.client.post('/api/user/2fa/enable');
      return response.data;
    } catch (error: any) {
      console.error('Enable 2FA API error:', error);

      return {
        success: false,
        error: error.response?.data?.message || 'Failed to enable two-factor authentication',
      };
    }
  }

  async disableTwoFactor(token: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await this.client.post('/api/user/2fa/disable', { token });
      return response.data;
    } catch (error: any) {
      console.error('Disable 2FA API error:', error);

      return {
        success: false,
        error: error.response?.data?.message || 'Failed to disable two-factor authentication',
      };
    }
  }

  async verifyTwoFactor(token: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await this.client.post('/api/user/2fa/verify', { token });
      return response.data;
    } catch (error: any) {
      console.error('Verify 2FA API error:', error);

      return {
        success: false,
        error: error.response?.data?.message || 'Invalid two-factor authentication code',
      };
    }
  }
}

// Create singleton instance
export const authApi = new AuthApiService();
